/**
 * Merchant-categories hooks (list / CRUD / reorder).
 *
 * The merchant-scoped category endpoints are not yet typed in OpenAPI —
 * every call goes through `as never` casts. TODO: drop once the schema
 * publishes them.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';
import { merchantOpsKeys, unwrap } from './keys';

export type CategoryResponse = components['schemas']['CategoryResponse'];

export interface MerchantCategoryRequest {
  name: string;
  iconUrl?: string | null;
  isActive?: boolean;
}

export function useMerchantCategories(
  merchantId: string | null,
  includeInactive: boolean,
): UseQueryResult<CategoryResponse[], Error> {
  return useQuery<CategoryResponse[], Error>({
    queryKey: merchantOpsKeys.categories(merchantId ?? 'none', includeInactive),
    enabled: merchantId !== null,
    queryFn: async () => {
      if (!merchantId) throw new Error('Selecione um estabelecimento');
      const { data, error } = await api.GET(
        '/api/v1/merchants/{id}/categories' as never,
        { params: { path: { id: merchantId }, query: { includeInactive } } } as never,
      );
      return unwrap(data as CategoryResponse[] | undefined, error);
    },
  });
}

interface MerchantCategoryVars {
  merchantId: string;
  body: MerchantCategoryRequest;
}
interface UpdateMerchantCategoryVars extends MerchantCategoryVars {
  categoryId: string;
}
interface DeleteMerchantCategoryVars {
  merchantId: string;
  categoryId: string;
}
interface ReorderMerchantCategoriesVars {
  merchantId: string;
  ids: string[];
}

export function useCreateMerchantCategory(): UseMutationResult<
  CategoryResponse,
  Error,
  MerchantCategoryVars
> {
  const qc = useQueryClient();
  return useMutation<CategoryResponse, Error, MerchantCategoryVars>({
    mutationFn: async ({ merchantId, body }) => {
      const { data, error } = await api.POST(
        '/api/v1/merchants/{id}/categories' as never,
        { params: { path: { id: merchantId } }, body } as never,
      );
      return unwrap(data as CategoryResponse | undefined, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}

export function useUpdateMerchantCategory(): UseMutationResult<
  CategoryResponse,
  Error,
  UpdateMerchantCategoryVars
> {
  const qc = useQueryClient();
  return useMutation<CategoryResponse, Error, UpdateMerchantCategoryVars>({
    mutationFn: async ({ merchantId, categoryId, body }) => {
      const { data, error } = await api.PUT(
        '/api/v1/merchants/{id}/categories/{categoryId}' as never,
        { params: { path: { id: merchantId, categoryId } }, body } as never,
      );
      return unwrap(data as CategoryResponse | undefined, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}

export function useDeleteMerchantCategory(): UseMutationResult<
  void,
  Error,
  DeleteMerchantCategoryVars
> {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteMerchantCategoryVars>({
    mutationFn: async ({ merchantId, categoryId }) => {
      const { error } = await api.DELETE(
        '/api/v1/merchants/{id}/categories/{categoryId}' as never,
        { params: { path: { id: merchantId, categoryId } } } as never,
      );
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}

export function useReorderMerchantCategories(): UseMutationResult<
  void,
  Error,
  ReorderMerchantCategoriesVars
> {
  const qc = useQueryClient();
  return useMutation<void, Error, ReorderMerchantCategoriesVars>({
    mutationFn: async ({ merchantId, ids }) => {
      const { error } = await api.POST(
        '/api/v1/merchants/{id}/categories/reorder' as never,
        { params: { path: { id: merchantId } }, body: { ids } } as never,
      );
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}
